// Quick-add composer for /reminders (pages/reminders.js): a focused title/
// submit row followed by compact due-date and visibility controls. All state lives on root
// (_reminderDraft / _reminderDraftDue) so Enter and the Add button share the
// exact same submit path (el.call('submitReminderDraft') — functions/reminders.js).

// Shared look for the three quick-set chips — a plain style object (not a
// registered component), spread into each declaration below. Mirrors the
// `_widgetCardSurface` convention in pages/main.js.
const _chipStyle = {
  tag: 'button',
  type: 'button',
  flexShrink: '0',
  minHeight: 'controlHCompact',
  padding: '0 Z1',
  fontSize: 'Z1',
  fontWeight: '700',
  color: 'title',
  background: 'overlay',
  border: 'none',
  round: 'radiusPill',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  // `overlayStrong` is a near-opaque sheet wash, not a state fill (§4.1) —
  // the hover wash is the shared `hover` token (§4.3).
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  ':focus-visible': { background: 'selected' },
  '@coarse': { minHeight: 'controlH' }
}

// Registered row shape for the @mention menu. Keeping this out of
// `childExtends` makes the render contract explicit and lets DOMQL resolve the
// item through the component registry on each state-driven menu render.
export const ReminderMentionOption = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center',
  gap: 'Z',
  padding: 'Y1 Z',
  minHeight: 'controlH',
  width: '100%',
  minWidth: '0',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  round: 'radiusControl',
  cursor: 'pointer',
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  ':focus-visible': { background: 'selected' },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    el.call('applyReminderMention', { name: s.name, email: s.email })
  },
  Avatar: {
    avatarType: 'initials',
    boxSize: 'avatarXs avatarXs',
    src: (el, s) => el.call('avatarUrl', s.name || s.email)
  },
  MName: {
    tag: 'span',
    fontSize: 'Z1',
    color: 'title',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s.name || s.email
  },
  MEmail: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'inactive',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s.email
  }
}

export const ReminderComposer = {
  extends: 'CommonCard',
  flow: 'y',
  gap: 'Z1',
  padding: '0',
  position: 'relative',
  // The draft stays intentionally two-tiered: a clear title/action row, then
  // a compact control tray. This keeps the add path stable as the column
  // narrows instead of interleaving date, scope, and submit controls.
  '@tabletS': { gap: 'Z' },
  '@appMobile': { gap: 'Z' },

  DraftRow: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    minWidth: '0',
    flexWrap: 'wrap',

    Input: {
      flex: '1 1 var(--spacing-G)',
      // A preferred input width makes the Add action wrap naturally inside
      // a narrow pane. A zero minimum lets the input itself fit a phone.
      minWidth: '0',
      maxWidth: '100%',
      minHeight: 'controlH',
      theme: 'field',
      background: 'hover',
      round: 'radiusControl',
      fontSize: 'Z2',
      padding: '0 A',
      'aria-label': (el) => el.call('polyglot', 'workspace.reminders.addAria'),
      placeholder: (el) => el.call('polyglot', 'workspace.reminders.composer.placeholder'),
      value: (el, s) => s.root?._reminderDraft || '',
      onInput: (e, el, s) => {
        s.root.update({ _reminderDraft: e.target.value })
        el.call('setReminderMentionQuery', e.target.value)
      },
      onKeydown: (e, el, s) => {
        if (e.key === 'Escape' && s.root?._reminderMentionQuery != null) {
          e.preventDefault()
          return s.root.update({ _reminderMentionQuery: null })
        }
        if (e.key !== 'Enter') return
        e.preventDefault()
        // Enter with the mention menu armed picks the top match; plain Enter
        // submits.
        const matches = el.call('reminderMentionMatches', s.root)
        if (s.root?._reminderMentionQuery != null && matches.length)
          return el.call('applyReminderMention', matches[0])
        el.call('submitReminderDraft')
      }
    },

    AddBtn: {
      tag: 'button',
      type: 'button',
      flexShrink: '0',
      theme: 'primary',
      border: 'none',
      minHeight: 'controlH',
      round: 'radiusPill',
      padding: '0 A',
      fontSize: 'Z2',
      fontWeight: '700',
      cursor: 'pointer',
      ':disabled': { opacity: '.55', cursor: 'not-allowed' },
      disabled: (el, s) => !(s.root?._reminderDraft || '').trim(),
      text: '{{ workspace.reminders.composer.add | polyglot }}',
      onClick: (e, el) => el.call('submitReminderDraft')
    }
  },

  // @mention autocomplete — anchored under the input, driven entirely by
  // root._reminderMentionQuery (null = closed). Rows are static-per-render
  // via childrenAs:'state' but their CLICK rides the row's own onClick,
  // which is safe here because the menu re-renders per keystroke anyway
  // (fresh mount each open — not the long-lived kanban-row shape the v3.14
  // binding quirk bites).
  MentionMenu: {
    hide: (el, s) =>
      s.root?._reminderMentionQuery == null || !el.call('reminderMentionMatches', s.root).length,
    position: 'absolute',
    top: '100%',
    left: 'Z',
    right: 'Z',
    marginTop: 'W',
    zIndex: '1200',
    flow: 'y',
    minWidth: '0',
    maxWidth: 'H',
    maxHeight: 'F',
    overflow: 'auto',
    padding: 'X',
    round: 'radiusControl',
    // Real bug: `background: 'dialog'` referenced a THEME name in a colour
    // slot — `dialog` is registered in designSystem/theme.js, not color.js
    // (zero matches), so the value emitted invalid CSS silently and this
    // floating menu had NO actual fill, relying on the border alone to read
    // (same class of bug as the tickets sweep's `background: 'surface'`
    // find). `theme: 'dialog'` is the real opaque fill.
    theme: 'common-box',
    shadow: 'overlay',

    childrenAs: 'state',
    children: (el, s) => el.call('reminderMentionMatches', s.root),
    childExtends: 'ReminderMentionOption'
  },

  ControlTray: {
    flow: 'x',
    align: 'center',
    gap: 'V',
    flexWrap: 'wrap',

    DueGroup: {
      flow: 'x',
      align: 'center',
      gap: 'V',
      flexWrap: 'wrap',

      TodayChip: {
        ..._chipStyle,
        text: '{{ workspace.reminders.composer.today | polyglot }}',
        onClick: (e, el, s) =>
          s.root.update({ _reminderDraftDue: el.call('reminderQuickDue', 'today') })
      },
      TomorrowChip: {
        ..._chipStyle,
        text: '{{ workspace.reminders.composer.tomorrow | polyglot }}',
        onClick: (e, el, s) =>
          s.root.update({ _reminderDraftDue: el.call('reminderQuickDue', 'tomorrow') })
      },
      NextWeekChip: {
        ..._chipStyle,
        text: '{{ workspace.reminders.composer.nextWeek | polyglot }}',
        onClick: (e, el, s) =>
          s.root.update({ _reminderDraftDue: el.call('reminderQuickDue', 'nextWeek') })
      },

      // Native picker styled as a chip — clicking it opens the browser's own
      // date/time UI directly (the "reveal" the quick chips don't need JS for).
      Input: {
        type: 'datetime-local',
        flexShrink: '0',
        minHeight: 'controlHCompact',
        fontSize: 'Z1',
        color: 'title',
        background: 'overlay',
        border: 'none',
        round: 'radiusPill',
        padding: '0 Z1',
        cursor: 'pointer',
        'aria-label': (el) => el.call('polyglot', 'workspace.reminders.composer.pickDate'),
        title: (el) => el.call('polyglot', 'workspace.reminders.composer.pickDate'),
        value: (el, s) => el.call('reminderDraftDueInputValue', s.root),
        onChange: (e, el, s) => el.call('setReminderDraftDue', e.target.value)
      },

      PendingDueChip: {
        hide: (el, s) => !s.root?._reminderDraftDue,
        flow: 'x',
        align: 'center',
        gap: 'W',
        flexShrink: '0',
        minHeight: 'controlHCompact',
        fontSize: 'Z1',
        fontWeight: '700',
        color: 'title',
        background: 'selected',
        round: 'radiusPill',
        padding: '0 Z1',

        Label: {
          tag: 'span',
          text: (el, s) => el.call('reminderDueLabel', s.root?._reminderDraftDue) || ''
        },
        RemoveBtn: {
          tag: 'button',
          type: 'button',
          flow: 'x',
          align: 'center center',
          background: 'transparent',
          border: 'none',
          padding: '0',
          minWidth: 'controlHCompact',
          minHeight: 'controlHCompact',
          round: 'radiusPill',
          cursor: 'pointer',
          color: 'title',
          ':hover': { background: 'hover' },
          ':active': { background: 'selected' },
          ':focus-visible': { background: 'selected' },
          'aria-label': (el) => el.call('polyglot', 'workspace.reminders.removeDueAria'),
          onClick: (e, el) => {
            e.stopPropagation()
            el.call('clearReminderDraftDue')
          },
          Icon: { name: 'crossmark', boxSize: 'iconXs' }
        }
      }
    },

    // Private ⇄ Shared visibility toggle — the same capsule language as the
    // schedule controls, with its state made explicit by both icon and text.
    VisibilityChip: {
      ..._chipStyle,
      flow: 'x',
      align: 'center',
      gap: 'W',
      color: 'title',
      background: (el, s) => (s.root?._reminderDraftShared ? 'selected' : 'overlay'),
      'aria-pressed': (el, s) => (s.root?._reminderDraftShared ? 'true' : 'false'),
      'aria-label': (el, s) =>
        el.call(
          'polyglot',
          s.root?._reminderDraftShared
            ? 'workspace.reminders.sharedChip'
            : 'workspace.reminders.visibility.private'
        ),
      title: (el, s) =>
        el.call(
          'polyglot',
          s.root?._reminderDraftShared
            ? 'workspace.reminders.sharedChip'
            : 'workspace.reminders.visibility.private'
        ),
      onClick: (e, el) => el.call('toggleReminderDraftShared'),
      Icon: {
        icon: (el, s) => (s.root?._reminderDraftShared ? 'globeOutline' : 'lockOutline'),
        boxSize: 'iconXs'
      },
      VisLabel: {
        tag: 'span',
        text: (el, s) =>
          el.call(
            'polyglot',
            s.root?._reminderDraftShared
              ? 'workspace.reminders.visibility.shared'
              : 'workspace.reminders.visibility.private'
          )
      }
    }
  }
}
